import type { PolyFeeSchedule, PolyRelayPosition } from '@/lib/polymarket/relay';
import { estimatePolyOrderFeeCents } from '@/lib/pricing/polyFees';

export interface UnrealizedCoverage {
  /** Complete account total. Null means at least one position is unpriced. */
  valueCents: number | null;
  /** Diagnostic subtotal over positions with every required accounting input. */
  pricedValueCents: number;
  pricedPositions: number;
  totalPositions: number;
  complete: boolean;
}

/** Turn per-position values into an honest account total. A null row means a
 * mark, fee schedule, or fee allocation is unknown; never silently turn that
 * row into zero and present the remaining subtotal as complete. */
export function summarizeUnrealizedCoverage(
  values: ReadonlyArray<number | null>,
): UnrealizedCoverage {
  let pricedValueCents = 0;
  let pricedPositions = 0;
  for (const value of values) {
    if (value == null) continue;
    pricedValueCents += value;
    pricedPositions += 1;
  }
  const totalPositions = values.length;
  const complete = pricedPositions === totalPositions;
  return {
    valueCents: complete ? pricedValueCents : null,
    pricedValueCents,
    pricedPositions,
    totalPositions,
    complete,
  };
}

/** Fee-aware Polymarket unrealized P&L with explicit coverage. A position is
 * priced only when its live mark, open-share fee allocation, and close-fee
 * schedule are all known. */
export function computePolyUnrealizedCoverage(args: {
  positions: ReadonlyArray<PolyRelayPosition>;
  schedules: ReadonlyMap<string, PolyFeeSchedule | null>;
  getNowCents: (position: PolyRelayPosition) => number | null;
}): UnrealizedCoverage {
  const { positions, schedules, getNowCents } = args;
  return summarizeUnrealizedCoverage(
    positions.map((position) => {
      const nowCents = getNowCents(position);
      if (nowCents == null || position.openFeesCents == null) return null;
      const closeFeeCents = estimatePolyOrderFeeCents({
        priceCents: nowCents,
        count: position.netCount,
        role: 'taker',
        schedule: schedules.get(position.ticker),
      });
      if (closeFeeCents == null) return null;
      const entryCostCents =
        position.costBasisCents ?? position.avgEntryPriceCents * position.netCount;
      return (
        nowCents * position.netCount -
        closeFeeCents -
        entryCostCents -
        position.openFeesCents
      );
    }),
  );
}
